import { Box } from '../primitives';
import { palette } from '../theme/palette';
import { usePortalPalette } from '../theme/usePortalPalette';
import { TOPBAR_CONTROL_HEIGHT } from '../styleUtils';

export type LanguageCode = 'en' | 'fr';

/**
 * Accessible names for the two segments, each written in the language it
 * switches *to* — a screen reader announcing "Passer au français" in French is
 * what a bilingual Canadian user expects, and it is why each segment also
 * carries its own `lang` attribute.
 */
const DEFAULT_SEGMENT_LABELS: Record<LanguageCode, string> = {
  en: 'Switch to English',
  fr: 'Passer au français',
};

export interface LanguageToggleProps {
  value: LanguageCode;
  onChange: (lang: LanguageCode) => void;
  /**
   * Accessible name for the group, e.g. `t('common.a11y.languageSwitcher')`.
   * Without it the pill is an unnamed `role="group"` and the two segments
   * announce as the bare strings "EN" / "FR".
   */
  groupLabel?: string;
  /** Per-segment accessible names, localized by the host app. */
  segmentLabels?: Partial<Record<LanguageCode, string>>;
  /** Tune the palette for a deep-teal (dark) surface — e.g. the app header. */
  onDark?: boolean;
  /**
   * `sm` is the compact inline pill. `md` stands at {@link TOPBAR_CONTROL_HEIGHT}
   * so it lines up with the search trigger and the account avatar beside it.
   */
  size?: 'sm' | 'md';
}

const LANGS: LanguageCode[] = ['en', 'fr'];

const SIZES = {
  sm: { height: 'auto', px: 1.25, py: 0.5, fontSize: 11 },
  md: { height: TOPBAR_CONTROL_HEIGHT, px: 1.5, py: 0, fontSize: 12 },
} as const;

/**
 * The EN/FR segmented pill — a single rounded control matching the mobile
 * `LanguagePill`. On a light surface it's a white pill with a teal-tint active
 * segment; the `onDark` variant sits on deep teal with a bright-teal active
 * segment that echoes the brand accent.
 */
export function LanguageToggle({
  value,
  onChange,
  onDark = false,
  size = 'sm',
  groupLabel = 'Language',
  segmentLabels,
}: LanguageToggleProps) {
  const portal = usePortalPalette();
  const dims = SIZES[size];
  const surface = onDark
    ? {
        background: palette.background.surfaceOnDarkSoft,
        border: palette.border.onDarkSubtle,
        activeBg: portal.brand.accentLight,
        activeText: portal.brand.rail,
        idleText: palette.text.onDarkSubtle,
        idleHover: palette.background.surfaceOnDarkMuted,
      }
    : {
        background: palette.background.paper,
        border: palette.border.lineStrong,
        activeBg: portal.brand.accentTint,
        activeText: portal.brand.accentDark,
        idleText: palette.text.muted,
        idleHover: palette.background.default,
      };

  return (
    <Box
      role="group"
      aria-label={groupLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        height: dims.height,
        flexShrink: 0,
        p: '2px',
        borderRadius: '999px',
        backgroundColor: surface.background,
        border: `1px solid ${surface.border}`,
      }}
    >
      {LANGS.map((lng) => {
        const active = value === lng;
        return (
          <Box
            key={lng}
            component="button"
            type="button"
            aria-pressed={active}
            aria-label={segmentLabels?.[lng] ?? DEFAULT_SEGMENT_LABELS[lng]}
            // The visible "EN"/"FR" text is in the target language, so tag it —
            // otherwise a French synthesiser reads "EN" as a French word and an
            // English one mangles "FR" (WCAG 3.1.2, Language of Parts).
            lang={lng}
            onClick={() => onChange(lng)}
            sx={{
              appearance: 'none',
              cursor: 'pointer',
              border: 'none',
              borderRadius: '999px',
              alignSelf: 'stretch',
              px: dims.px,
              py: dims.py,
              fontFamily: "'Manrope', sans-serif",
              fontSize: dims.fontSize,
              fontWeight: 600,
              letterSpacing: '0.06em',
              lineHeight: 1,
              transition: 'background-color 200ms, color 200ms',
              backgroundColor: active ? surface.activeBg : 'transparent',
              color: active ? surface.activeText : surface.idleText,
              '&:hover': active ? undefined : { backgroundColor: surface.idleHover },
            }}
          >
            {lng.toUpperCase()}
          </Box>
        );
      })}
    </Box>
  );
}
